import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";

import { POSTAL_CODE_MAX_LENGTH } from "./constants";
import { useAddressLabels } from "./hooks";
import { countriesWithNoSubdivisions } from "./utils";

export function useAddressSchema(
  options: {
    streetAddressFieldName?: string;
  } = {}
) {
  const { t } = useTranslation();
  const { streetAddressFieldName = "street_address" } = options;

  const { getLabels } = useAddressLabels();

  return useMemo(() => {
    const postalCodeRules: Record<
      string,
      { pattern: RegExp; message: string }
    > = {
      US: {
        pattern: /^\d{5}(-\d{4})?$/,
        message: t("Enter a valid ZIP code (e.g. 90210 or 90210-1234)"),
      },
      CA: {
        pattern:
          /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d$/i,
        message: t("Enter a valid postal code (e.g. K1A 0B1)"),
      },
      AU: {
        pattern: /^\d{4}$/,
        message: t("Enter a valid 4-digit postcode (e.g. 2000)"),
      },
      GB: {
        pattern: /^(GIR ?0AA|[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/i,
        message: t("Enter a valid postcode (e.g. SW1A 2AA)"),
      },
    };

    return object().shape({
      unit_number: string().when(["country"], ([country], schema) =>
        schema.label(getLabels(country)["unit_number"])
      ),
      [streetAddressFieldName]: string().trim().required().label(t("Address")),
      locality: string()
        .trim()
        .when(["country"], ([country], schema) =>
          schema.required().label(getLabels(country)["locality"])
        ),
      administrative_division_2: string()
        .nullable()
        .trim()
        .label(getLabels()["administrative_division_2"])
        .when(["country"], ([country], schema) => {
          if (["US", "GB"].includes(country)) {
            return schema.required();
          }
          return schema;
        }),
      administrative_division_1: string().when(
        ["country"],
        ([country], schema) => {
          if (country && !countriesWithNoSubdivisions.includes(country)) {
            return schema
              .label(getLabels(country)["administrative_division_1"])
              .required();
          }
          return schema.notRequired();
        }
      ),
      country: string().required().label(t("Country")),
      postal_code: string()
        .trim()
        .max(
          POSTAL_CODE_MAX_LENGTH,
          t("Must be at most {{max}} characters", {
            max: POSTAL_CODE_MAX_LENGTH,
          }) ?? ""
        )
        .when(["country"], ([country = "default"], schema) => {
          const base = schema
            .required()
            .label(getLabels(country)["postal_code"]);
          const rule = postalCodeRules[country];
          return rule ? base.matches(rule.pattern, rule.message) : base;
        }),
    });
  }, [getLabels, t, options.streetAddressFieldName]);
}
